// Visitor-facing widget strings. Every key must exist in every language — `Strings`
// is derived from `en`, so a missing `sr` key is a type error.

export type Lang = "en" | "sr";

const en = {
    // launcher
    "launcher.openOnline": "Open chat (agents online)",
    "launcher.openOffline": "Open chat (agents offline, leave a message)",
    "launcher.titleOnline": "Chat with us — we're online",
    "launcher.titleOffline": "We're away — leave us a message",
    "launcher.resetHint": " (double-click to reset position)",

    // header
    "header.defaultTitle": "Support",
    "header.online": "We're online — we usually reply in a few minutes",
    "header.offline": "We're away — leave us a message",
    "header.away": "Back in a few minutes — write to us, we'll reply",
    "launcher.openAway": "Open chat (agents back in a few minutes)",
    "launcher.titleAway": "We'll be right back — write to us",
    "contact.awayTitle": "We'll be right back",
    "contact.awayText": "An agent will reply here in a few minutes. Can't wait? Leave your email and we'll send the reply there too.",
    "composer.away": "Write to us — we'll reply in a few minutes…",
    "header.reconnecting": "Reconnecting…",
    "header.sendTranscript": "Send transcript",
    "header.endChat": "End chat",
    "header.close": "Close chat window",

    // what's new
    "whatsNew.aria": "What's new",
    "whatsNew.badge": "NEW",
    "whatsNew.title": "New in chat",
    "whatsNew.moveTitle": "Move the chat button",
    "whatsNew.moveText":
        "Is the chat button in your way? Drag it anywhere on the screen. We'll remember where you put it. Double-click it to move it back.",
    "whatsNew.onlineTitle": "See when we're online",
    "whatsNew.onlineText":
        "A green dot on the chat button means an agent is online. A grey dot means we're away, and you can leave us a message.",
    "whatsNew.ok": "Got it",

    // transcript / close sheets
    "transcript.title": "Send transcript",
    "transcript.placeholder": "you@email.com",
    "transcript.hint": "We'll email you the full conversation transcript.",
    "close.title": "Close chat?",
    "close.hint": "This will end the conversation.",
    "close.confirm": "Yes, close",

    // common buttons
    "btn.cancel": "Cancel",
    "btn.send": "Send",
    "btn.sending": "Sending…",
    "btn.skip": "Skip",
    "btn.submit": "Submit",
    "btn.sendMessage": "Send message",

    // messages
    "msg.agent": "Agent",
    "msg.imageAlt": "Image attachment",
    "msg.attachment": "Attachment",
    "msg.status.sending": "sending…",
    "msg.status.seen": "seen",
    "msg.status.failed": "failed",
    "msg.status.sent": "sent",
    "typing.bot": "AI Assistant is typing…",
    "typing.agent": "Agent is typing…",

    // system notices (server sends English text + metadata; we render from metadata)
    "system.escalate": "Thanks for reaching out! I'll get a team member to help you with this.",
    "system.escalateQueue":
        "Thanks for reaching out! I'll get a team member to help you with this. You're #{pos} in line — estimated wait ~{min} min.",
    "system.closedByAgent": "This conversation was closed by an agent.",
    "system.closedByVisitor": "This conversation was closed by the visitor.",
    "system.autoClose": "This conversation was closed due to inactivity.",

    // contact form (after escalation)
    "contact.title": "Leave your details? (optional)",
    "contact.text": "So an agent knows who's asking and can reach you if you leave.",
    "contact.name": "Your name",
    "contact.emailOptional": "Your email (optional)",
    "contact.offlineTitle": "We're away right now",
    "contact.offlineText": "Leave your email and we'll send our reply there if you're no longer on the site.",

    // offline form
    "offline.title": "Leave us a message",
    "offline.text": "No agents are online right now. Leave your message and we'll get back to you.",
    "offline.email": "Your email",
    "offline.message": "How can we help?",
    "offline.received": "Message received. We'll reply here, and by email to {email} if you've left.",
    "offline.receivedNoEmail": "Message received. We'll reply here as soon as we're back.",
    // offline form with ticketing: the message became a ticket answered by email
    "ticket.receivedTitle": "Request received",
    "ticket.receivedText": "Your request number is #{n}. We'll reply to {email}.",
    "ticket.another": "Send another message",
    "system.ticket": "We opened ticket #{n} for this. We'll follow up by email at {email}.",

    // rating
    "rating.title": "How was your chat?",
    "rating.star": "Rate {n} out of 5",
    "rating.comment": "Anything you'd like to add? (optional)",
    "rating.later": "Maybe later",
    "closed.ended": "This chat has ended.",
    "preChat.title": "Before we start",
    "preChat.text": "Tell us who you are so we can get back to you if the chat gets cut off.",
    "preChat.name": "Your name",
    "preChat.nameOptional": "Your name (optional)",
    "preChat.email": "Your email",
    "preChat.emailOptional": "Your email (optional)",
    "preChat.start": "Start chat",
    "greeting.default": "Hi 👋 Have a question? We're here to help.",
    "greeting.close": "Close",
    "btn.newChat": "Start a new chat",
    "btn.startingNewChat": "Starting…",

    // composer
    "composer.attach": "Attach file",
    "composer.removeAttachment": "Remove attachment",
    "composer.connecting": "Connecting…",
    "composer.closed": "Chat closed.",
    "composer.offline": "No agents online — we'll reply as soon as possible…",
    "composer.withFile": "Add a message or send the file…",
    "composer.type": "Type a message…",

    // notices
    "notice.fileUploaded": "File uploaded.",
    "notice.fileFailed": "File upload failed.",
    "notice.chatClosed": "Chat closed.",
    "notice.closeFailed": "Couldn't close the chat.",
    "notice.ratingThanks": "Thanks for the feedback!",
    "notice.ratingFailed": "Couldn't send your rating.",
    "notice.contactThanks": "Thanks!",
    "notice.contactFailed": "Couldn't save your info.",
    "notice.invalidEmail": "Enter a valid email.",
    "notice.transcriptSent": "Transcript sent.",
    "notice.transcriptFailed": "Couldn't send the transcript.",
    "notice.enterMessage": "Enter your message.",
    "notice.messageSent": "Message sent.",
    "notice.messageFailed": "Couldn't send the message.",
};

export type StringKey = keyof typeof en;
type Strings = Record<StringKey, string>;

const sr: Strings = {
    "launcher.openOnline": "Otvori čet (agenti su dostupni)",
    "launcher.openOffline": "Otvori čet (agenti nisu dostupni, ostavite poruku)",
    "launcher.titleOnline": "Pišite nam — tu smo",
    "launcher.titleOffline": "Trenutno nismo tu — ostavite poruku",
    "launcher.resetHint": " (dupli klik vraća dugme na mesto)",

    "header.defaultTitle": "Podrška",
    "header.online": "Tu smo — obično odgovaramo za par minuta",
    "header.offline": "Trenutno nismo tu — ostavite poruku",
    "header.away": "Vraćamo se za par minuta — pišite, odgovorićemo",
    "launcher.openAway": "Otvori čet (agenti se vraćaju za par minuta)",
    "launcher.titleAway": "Vraćamo se za par minuta — pišite nam",
    "contact.awayTitle": "Vraćamo se za par minuta",
    "contact.awayText": "Agent će vam odgovoriti ovde za par minuta. Ne možete da čekate? Ostavite email i poslaćemo odgovor i tamo.",
    "composer.away": "Pišite nam — odgovorićemo za par minuta…",
    "header.reconnecting": "Ponovno povezivanje…",
    "header.sendTranscript": "Pošalji prepisku",
    "header.endChat": "Završi razgovor",
    "header.close": "Zatvori prozor četa",

    "whatsNew.aria": "Novo",
    "whatsNew.badge": "NOVO",
    "whatsNew.title": "Novo u četu",
    "whatsNew.moveTitle": "Pomerite dugme za čet",
    "whatsNew.moveText":
        "Smeta vam dugme za čet? Prevucite ga bilo gde na ekranu i zapamtićemo gde ste ga ostavili. Dupli klik ga vraća na mesto.",
    "whatsNew.onlineTitle": "Vidite kada smo tu",
    "whatsNew.onlineText":
        "Zelena tačka na dugmetu znači da je agent dostupan. Siva tačka znači da trenutno nismo tu, a vi nam možete ostaviti poruku.",
    "whatsNew.ok": "Važi",

    "transcript.title": "Pošalji prepisku",
    "transcript.placeholder": "vi@email.com",
    "transcript.hint": "Poslaćemo vam celu prepisku razgovora na email.",
    "close.title": "Završiti razgovor?",
    "close.hint": "Ovim se razgovor završava.",
    "close.confirm": "Da, završi",

    "btn.cancel": "Otkaži",
    "btn.send": "Pošalji",
    "btn.sending": "Šalje se…",
    "btn.skip": "Preskoči",
    "btn.submit": "Pošalji",
    "btn.sendMessage": "Pošalji poruku",

    "msg.agent": "Agent",
    "msg.imageAlt": "Slika u prilogu",
    "msg.attachment": "Prilog",
    "msg.status.sending": "šalje se…",
    "msg.status.seen": "viđeno",
    "msg.status.failed": "nije poslato",
    "msg.status.sent": "poslato",
    "typing.bot": "AI asistent kuca…",
    "typing.agent": "Agent kuca…",

    "system.escalate": "Hvala što ste nam se obratili! Povezaću vas sa nekim iz tima.",
    "system.escalateQueue":
        "Hvala što ste nam se obratili! Povezaću vas sa nekim iz tima. Vi ste #{pos} u redu — procenjeno čekanje ~{min} min.",
    "system.closedByAgent": "Agent je završio ovaj razgovor.",
    "system.closedByVisitor": "Posetilac je završio ovaj razgovor.",
    "system.autoClose": "Razgovor je zatvoren zbog neaktivnosti.",

    "contact.title": "Ostavite podatke? (opciono)",
    "contact.text": "Da agent zna ko pita i da može da vam se javi ako odete.",
    "contact.name": "Vaše ime",
    "contact.emailOptional": "Vaš email (opciono)",
    "contact.offlineTitle": "Trenutno nismo tu",
    "contact.offlineText": "Ostavite email i poslaćemo vam odgovor tamo ako više ne budete na sajtu.",

    "offline.title": "Ostavite nam poruku",
    "offline.text": "Trenutno nijedan agent nije dostupan. Ostavite poruku i javićemo vam se.",
    "offline.email": "Vaš email",
    "offline.message": "Kako možemo da pomognemo?",
    "offline.received": "Poruka je primljena. Odgovorićemo ovde, a i na {email} ako vas ne bude na sajtu.",
    "offline.receivedNoEmail": "Poruka je primljena. Odgovorićemo ovde čim budemo tu.",
    "ticket.receivedTitle": "Upit je primljen",
    "ticket.receivedText": "Broj vašeg upita je #{n}. Odgovor stiže na {email}.",
    "ticket.another": "Pošalji još jednu poruku",
    "system.ticket": "Otvorili smo upit #{n} za ovo. Javićemo vam se mailom na {email}.",

    "rating.title": "Kako je prošao razgovor?",
    "rating.star": "Ocena {n} od 5",
    "rating.comment": "Želite li nešto da dodate? (opciono)",
    "rating.later": "Možda kasnije",
    "closed.ended": "Razgovor je završen.",
    "preChat.title": "Pre nego što počnemo",
    "preChat.text": "Recite nam ko ste, da bismo vam se javili ako se razgovor prekine.",
    "preChat.name": "Vaše ime",
    "preChat.nameOptional": "Vaše ime (opciono)",
    "preChat.email": "Vaš email",
    "preChat.emailOptional": "Vaš email (opciono)",
    "preChat.start": "Započni razgovor",
    "greeting.default": "Zdravo 👋 Imate pitanje? Tu smo da pomognemo.",
    "greeting.close": "Zatvori",
    "btn.newChat": "Započni novi razgovor",
    "btn.startingNewChat": "Pokreće se…",

    "composer.attach": "Priloži fajl",
    "composer.removeAttachment": "Ukloni prilog",
    "composer.connecting": "Povezivanje…",
    "composer.closed": "Razgovor je završen.",
    "composer.offline": "Agenti trenutno nisu tu — odgovorićemo čim budemo mogli…",
    "composer.withFile": "Dodajte poruku ili pošaljite fajl…",
    "composer.type": "Napišite poruku…",

    "notice.fileUploaded": "Fajl je otpremljen.",
    "notice.fileFailed": "Otpremanje fajla nije uspelo.",
    "notice.chatClosed": "Razgovor je završen.",
    "notice.closeFailed": "Razgovor nije mogao da se završi.",
    "notice.ratingThanks": "Hvala na oceni!",
    "notice.ratingFailed": "Ocena nije poslata.",
    "notice.contactThanks": "Hvala!",
    "notice.contactFailed": "Podaci nisu sačuvani.",
    "notice.invalidEmail": "Unesite ispravan email.",
    "notice.transcriptSent": "Prepiska je poslata.",
    "notice.transcriptFailed": "Prepiska nije poslata.",
    "notice.enterMessage": "Unesite poruku.",
    "notice.messageSent": "Poruka je poslata.",
    "notice.messageFailed": "Poruka nije poslata.",
};

const STRINGS: Record<Lang, Strings> = { en, sr };

// Browser languages that should get the Serbian (Latin) UI.
const SR_PREFIXES = ["sr", "hr", "bs", "sh", "cnr"];

export function resolveLang(setting?: string | null): Lang {
    if (setting === "en" || setting === "sr") return setting;
    try {
        const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
        for (const l of langs) {
            const base = String(l || "").toLowerCase().split("-")[0];
            if (SR_PREFIXES.includes(base)) return "sr";
            if (base === "en") return "en";
        }
    } catch {
        /* ignore */
    }
    return "en";
}

export type T = (key: StringKey, vars?: Record<string, string | number>) => string;

export function makeT(lang: Lang): T {
    const dict = STRINGS[lang];
    return (key, vars) => {
        let s = dict[key] ?? en[key];
        if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
        return s;
    };
}

export const DATE_LOCALE: Record<Lang, string> = { en: "en-GB", sr: "sr-Latn-RS" };
